/**
 * Un icono por linea de producto, para el menu del catalogo.
 *
 * Se eligen por el `slug` de la linea y no por su id: el slug es lo que se lee
 * y lo que cualquiera reconoce al agregar una linea nueva. Una linea sin icono
 * asignado recibe la caja generica, nunca un hueco: las lineas se pueden crear
 * sin tocar este archivo, y el menu no puede romperse por eso.
 *
 * Van en un archivo aparte de `Icono.tsx` para no engordar el conjunto de
 * iconos de la interfaz con dibujos que solo se ven en un lugar. Mismas reglas:
 * SVG inline, `currentColor`, decorativos (`aria-hidden`): el nombre lo pone
 * siempre el texto de al lado.
 *
 * Hay mas iconos que lineas a proposito: `montana` y `bandu-kids` son lineas que
 * la marca ya trabaja en sus piezas graficas y todavia no tienen productos en el
 * catalogo. El dia que se creen, su icono ya esta.
 */
import type { ReactElement } from 'react';

const TRAZOS: Record<string, ReactElement> = {
  // Un remo cruzado sobre el agua.
  rafting: (
    <>
      <path d="M17 3.5l3.5 3.5-2.2 2.2a2.5 2.5 0 0 1-3.5-3.5z" />
      <path d="M15.6 8.4L6 18" />
      <path d="M3 20.5c1.5-1.3 3-1.3 4.5 0s3 1.3 4.5 0 3-1.3 4.5 0 3 1.3 4.5 0" />
    </>
  ),
  // Un kayak visto desde arriba, con su pala doble.
  kayak: (
    <>
      <path d="M12 2.5c2.6 3 3.6 6 3.6 9.5s-1 6.5-3.6 9.5c-2.6-3-3.6-6-3.6-9.5s1-6.5 3.6-9.5z" />
      <circle cx="12" cy="12" r="1.8" />
      <path d="M3.5 8.5l17 7M2.5 7l2 3M19.5 14l2 3" />
    </>
  ),
  // Una balsa de rafting vista desde arriba, con sus bancos.
  embarcaciones: (
    <>
      <rect x="3" y="6" width="18" height="12" rx="6" />
      <rect x="6.5" y="9.2" width="11" height="5.6" rx="2.8" />
      <path d="M10 9.2v5.6M14 9.2v5.6" />
    </>
  ),
  // Una carpa con su entrada.
  'campamento-base': (
    <>
      <path d="M12 4L2.5 19.5h19z" />
      <path d="M12 19.5v-6l-3 6M12 13.5l3 6M12 4v-1.5" />
    </>
  ),
  // Una bateria con su rayo.
  'energia-portatil': (
    <>
      <rect x="4" y="7" width="16" height="12" rx="2" />
      <path d="M9 7V4.5h6V7" />
      <path d="M12.8 9.5l-2.8 4h4l-2.8 4" />
    </>
  ),
  montana: (
    <>
      <path d="M2.5 19.5l6.5-11 4 6.5 2.5-3.5 6 8z" />
      <path d="M7 12l2 1.5 2-1.5" />
    </>
  ),
  'bandu-kids': (
    <>
      <circle cx="12" cy="7.5" r="3.5" />
      <path d="M5.5 20.5c0-4 2.9-6.5 6.5-6.5s6.5 2.5 6.5 6.5" />
      <path d="M9.5 5.5c.8-1.3 1.6-2 2.5-2s1.7.7 2.5 2" />
    </>
  ),
};

const GENERICO = (
  <>
    <path d="M3 7.5l9-4.5 9 4.5v9L12 21l-9-4.5z" />
    <path d="M3 7.5l9 4.5 9-4.5M12 12v9" />
  </>
);

export interface IconoLineaProps {
  slug: string;
  className?: string;
}

export function IconoLinea({ slug, className }: IconoLineaProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...(className !== undefined ? { className } : {})}
    >
      {TRAZOS[slug] ?? GENERICO}
    </svg>
  );
}
